/**
 * Availability rules for membership-granted treatments: a membership treatment
 * is only redeemable once its reward group's milestone has been reached, unless
 * the group grants rewards immediately (no delay before the first reward).
 *
 * Shared by the wallet treatments page and the post-check-in redemption prompt
 * so both surfaces agree on which membership treatments are redeemable.
 */

export interface MembershipRewardGroupAvailability {
  id: string;
  receivedAt?: string | null;
  monthsBeforeFirstReward: number;
  billingPeriodsBeforeFirstReward: number;
  availableTreatmentsAmount: number;
  availableAt?: string | null;
  membershipRewards?: { packageId: string }[] | null;
}

export interface MembershipTreatmentIdentity {
  packageId: string;
  membershipRewardGroupIds?: string[] | null;
}

export const isMembershipRewardAvailable = (
  treatment: MembershipTreatmentIdentity,
  allRewardGroups: MembershipRewardGroupAvailability[],
): boolean => {
  // Find matching reward group by membershipRewardGroupIds or packageId
  const rewardGroup =
    allRewardGroups.find((rg) => treatment.membershipRewardGroupIds?.includes(rg.id)) ??
    allRewardGroups.find((rg) =>
      rg.membershipRewards?.some((r) => r.packageId === treatment.packageId),
    );

  // If the reward group is not found or the milestone was already unlocked, show the treatment
  if (!rewardGroup || rewardGroup.receivedAt) {
    return true;
  }

  // Immediate rewards (0 delay) are always available
  const isImmediate =
    rewardGroup.monthsBeforeFirstReward === 0 || rewardGroup.billingPeriodsBeforeFirstReward === 0;

  if (isImmediate) {
    return true;
  }

  const hasAvailable = rewardGroup.availableTreatmentsAmount > 0;
  const datePassed = rewardGroup.availableAt
    ? new Date(rewardGroup.availableAt) <= new Date()
    : false;

  return hasAvailable || datePassed;
};
